const GIFT_NOTE = 'gift_note';
const COOKIE_DAYS_EXPIRE = 7;
const REGEX_VALIDATORS = {
  // Test for hardcoded backslash
  backslashTest: /\\/,
  // Test for special chars and new lines: | < > " ' & \r \n
  specialCharsTest: /([\||\r|\n|\<|\>|\"|\'|\&])/,
  // Test for emojis
  emojiTest: /([\u203C-\u3299]|[\uD83C-\uDBFF\uDC00-\uDFFF]+|[\u2600-\u27BF])/,
};

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.submitBtn = document.querySelector('button#checkout[type="submit"]');
        this.noteErrors = document.querySelectorAll('.note-error');

        this.addEventListener(
          'input',
          debounce((event) => {
            let value = event.target.value;
            if (
              REGEX_VALIDATORS.backslashTest.test(value) ||
              REGEX_VALIDATORS.specialCharsTest.test(value) ||
              REGEX_VALIDATORS.emojiTest.test(value)
            ) {
              this.showFieldError(event.target.name);
              if (this.submitBtn) this.submitBtn.disabled = true;

              return;
            }

            this.hideFieldError(event.target.name);

            // Only enable the submit button if all note-error elements are hidden
            const areAllErrorsHidden = Array.from(this.noteErrors).every((el) => el.classList.contains('hidden'));
            if (this.submitBtn) this.submitBtn.disabled = !areAllErrorsHidden;

            const body = JSON.stringify({ note: value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }

      showFieldError(inputName) {
        const errorElement = document.querySelector(`[data-field="${inputName}"]`);
        if (errorElement) {
          errorElement.classList.remove('hidden');
        }
      }

      hideFieldError(inputName) {
        const errorElement = document.querySelector(`[data-field="${inputName}"]`);
        if (errorElement) {
          errorElement.classList.add('hidden');
        }
      }
    }
  );
}

if (!customElements.get('cart-gift-wrap')) {
  customElements.define(
    'cart-gift-wrap',
    class CartGiftWrap extends HTMLElement {
      constructor() {
        super();

        this.isLoading = false;
        this.giftNote = '';
        this.giftWrapProducts = [];
        this.showGiftNote = false;
        this.giftAgreeCheckbox = this.querySelector('[name="gift-agree"]');
        this.giftNoteContainer = this.querySelector('.gift-note-container');
        this.giftNoteElement = this.querySelector('[name="gift-note"]');
        this.giftProductsContainer = this.querySelector('.gift__products');
        this.giftWrapProductsJSON = this.querySelector('.gift__wrap-products-json');
        this.nullGiftWrapProductRadio = this.querySelector('#gift-product-null');
        this.submitBtn = document.querySelector('button#checkout[type="submit"]');
        this.noteErrors = document.querySelectorAll('.note-error');

        this.initDefault();

        if (this.giftAgreeCheckbox) {
          this.giftAgreeCheckbox.addEventListener('change', (event) => {
            this.showGiftNote = event.target.checked;
            if (this.giftNoteContainer) {
              if (!this.showGiftNote) {
                this.hideFieldError('gift-note');
                this.giftNote = '';
                this.setCookie(GIFT_NOTE, '', COOKIE_DAYS_EXPIRE);
                this.giftNoteElement.value = '';

                // Set null gift wrap product as selected
                if (this.nullGiftWrapProductRadio) this.nullGiftWrapProductRadio.checked = true;

                // Only enable the submit button if all note-error elements are hidden
                const areAllErrorsHidden = Array.from(this.noteErrors).every((el) => el.classList.contains('hidden'));
                if (this.submitBtn) this.submitBtn.disabled = !areAllErrorsHidden;

                // Clear gift wrap product and gift note
                this.updateGiftNoteToCart();
              }

              this.giftNoteContainer.classList.toggle('hidden', !this.showGiftNote);
            }
          });
        }

        if (this.giftNoteElement) {
          this.giftNoteElement.addEventListener(
            'input',
            debounce((event) => {
              let value = event.target.value;
              if (
                REGEX_VALIDATORS.backslashTest.test(value) ||
                REGEX_VALIDATORS.specialCharsTest.test(value) ||
                REGEX_VALIDATORS.emojiTest.test(value)
              ) {
                this.showFieldError(event.target.name);
                if (this.submitBtn) this.submitBtn.disabled = true;

                return;
              }

              this.hideFieldError(event.target.name);

              // Only enable the submit button if all note-error elements are hidden
              const areAllErrorsHidden = Array.from(this.noteErrors).every((el) => el.classList.contains('hidden'));
              if (this.submitBtn) this.submitBtn.disabled = !areAllErrorsHidden;

              this.giftNote = value;
              this.updateGiftNoteToCart();
            }, ON_CHANGE_DEBOUNCE_TIMER)
          );
        }

        if (this.nullGiftWrapProductRadio) {
          this.nullGiftWrapProductRadio.addEventListener('change', (event) => {
            this.removeGiftProduct();
          });

          const nullGiftWrapCard = this.nullGiftWrapProductRadio.closest('.gift__product');
          if (nullGiftWrapCard) {
            nullGiftWrapCard.addEventListener('click', () => {
              if (!this.nullGiftWrapProductRadio.checked) {
                this.nullGiftWrapProductRadio.checked = true;
                this.nullGiftWrapProductRadio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          }
        }

        if (this.giftWrapProductsJSON) {
          const giftWrapProductsString = this.giftWrapProductsJSON.textContent.trim();
          const giftWrapProductsArray = JSON.parse(giftWrapProductsString);

          this.giftWrapProducts = giftWrapProductsArray.slice(0, 2);
          this.giftWrapProducts.forEach((product, idx) => {
            const div = document.createElement('div');
            div.className = 'gift__product';

            const input = document.createElement('input');
            input.type = 'radio';
            input.name = 'product-gift-wrap';
            input.className = '';
            input.id = `gift-product-${product.id}`;
            input.value = product.id;

            input.addEventListener('change', (event) => {
              this.addGiftProductToCart(product.variants[0].id, product.properties);
            });

            const label = document.createElement('label');
            label.className = 'gift__product-label';
            label.setAttribute('for', input.id);
            label.textContent = product.label || product.handle || `Gift Product ${idx + 1}`;

            const img = document.createElement('img');
            img.className = 'gift__product-image';
            img.setAttribute('src', product.image);

            div.appendChild(input);
            div.appendChild(img);
            div.appendChild(label);

            div.addEventListener('click', (e) => {
              const input = div.querySelector('input[type="radio"]');
              if (input && !input.checked) {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });

            this.giftProductsContainer.appendChild(div);
          });
        }
      }

      showFieldError(inputName) {
        const errorElement = document.querySelector(`[data-field="${inputName}"]`);
        if (errorElement) {
          errorElement.classList.remove('hidden');
        }
      }

      hideFieldError(inputName) {
        const errorElement = document.querySelector(`[data-field="${inputName}"]`);
        if (errorElement) {
          errorElement.classList.add('hidden');
        }
      }

      getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
          return decodeURIComponent(parts.pop().split(';').shift());
        }
        return null;
      }

      setCookie(name, value, days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/`;
      }

      async initDefault() {
        try {
          const response = await fetch('/cart.js');
          if (!response.ok) throw new Error('Network response was not ok');
          const cart = await response.json();

          const cartGiftMessage = cart.attributes['Gift Note'];
          const cartItems = cart.items;
          const defaultGiftWrapProduct = cartItems.find((item) =>
            Object.keys(item.properties).includes('_isGiftWrapping')
          );

          if (!!defaultGiftWrapProduct) {
            const actualGiftWrapProduct = this.giftWrapProducts.find(
              (product) => product.variants[0].id === defaultGiftWrapProduct.id
            );

            if (!!actualGiftWrapProduct) {
              this.defaultGiftWrap = actualGiftWrapProduct;
              const defaultGiftWrapElement = this.querySelector(`#gift-product-${this.defaultGiftWrap.id}`);

              if (defaultGiftWrapElement) {
                defaultGiftWrapElement.checked = true;
              }
            }

            this.showGiftNote = true;
            if (this.giftAgreeCheckbox) {
              this.giftAgreeCheckbox.checked = this.showGiftNote;
            }
            if (this.giftNoteContainer) {
              this.giftNoteContainer.classList.toggle('hidden', !this.showGiftNote);
            }
          } else {
            if (this.nullGiftWrapProductRadio) {
              this.nullGiftWrapProductRadio.checked = true;
            }
          }

          if ((!!cartGiftMessage && cartGiftMessage.lenght > 0) || this.getCookie(GIFT_NOTE)) {
            this.giftNote = this.getCookie(GIFT_NOTE);
            if (this.giftNoteElement) {
              this.giftNoteElement.value = this.giftNote;
            }

            this.showGiftNote = true;
            if (this.giftAgreeCheckbox) {
              this.giftAgreeCheckbox.checked = this.showGiftNote;
            }
            if (this.giftNoteContainer) {
              this.giftNoteContainer.classList.toggle('hidden', !this.showGiftNote);
            }
          }
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      }

      async getCartItemsGiftWrappingLineItemKeys() {
        try {
          const response = await fetch('/cart.js');
          if (!response.ok) throw new Error('Network response was not ok');
          const cart = await response.json();

          const cartItems = cart.items;
          const cartItemsGiftWrapping = cartItems.filter((item) =>
            Object.keys(item.properties).join().includes('_isGiftWrapping')
          );
          return cartItemsGiftWrapping.map((item) => item.key).filter((item) => item);
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      }

      async addGiftProductToCart(variantId, properties) {
        try {
          await this.removeGiftProduct(false);

          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                'gift-wrapping': true,
              },
              items: [
                {
                  quantity: 1,
                  id: variantId,
                  properties: {
                    _isGiftWrapping: 'true',
                    properties,
                  },
                },
              ],
            }),
          });
          location.reload();
        } catch (error) {
          console.error('Error:', error);
        }
      }

      async removeGiftProduct(performReload = true) {
        this.isLoading = true;

        try {
          let giftPostData = {
            updates: {},
          };

          let cartItemsGiftWrappingLineItemKeys = await this.getCartItemsGiftWrappingLineItemKeys();

          cartItemsGiftWrappingLineItemKeys.forEach((key) => {
            giftPostData.updates[`${key}`] = 0;
          });

          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(giftPostData),
          });

          if (performReload) {
            location.reload();
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          this.isLoading = false;
        }
      }

      async updateGiftNoteToCart() {
        this.isLoading = true;

        try {
          let giftPostData = {
            attributes: {
              'Gift Note': '',
              'gift-wrapping': false,
            },
            updates: {},
          };

          let cartItemsGiftWrappingLineItemKeys = await this.getCartItemsGiftWrappingLineItemKeys();

          // If we show the gift wrapping, AND there is a gift note message, add it to the cart
          if (this.showGiftNote) {
            if (this.giftNote.length > 0) {
              giftPostData.attributes['Gift Note'] = this.giftNote;
              giftPostData.attributes['gift-wrapping'] = true;
              this.setCookie(GIFT_NOTE, this.giftNote, COOKIE_DAYS_EXPIRE);
            } else {
              this.setCookie(GIFT_NOTE, '', COOKIE_DAYS_EXPIRE);
            }
          } else {
            // if we do NOT add any gift wrapping, remove any gift wrap products and notes; Gift notes default to empty.
            cartItemsGiftWrappingLineItemKeys.forEach((key) => {
              giftPostData.updates[`${key}`] = 0;
            });
            this.setCookie(GIFT_NOTE, '', COOKIE_DAYS_EXPIRE);
          }

          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(giftPostData),
          });
        } catch (error) {
          console.error('Error:', error);
        } finally {
          this.isLoading = false;
        }
      }
    }
  );
}
